import { StatusToneEnum } from '@ztechapps/common.contract'
import { HorizontalStack, Icon, ShowIf, StatusColorBadge, TooltipPopOver } from '@ztechapps/webui.util'
import { QuantityWithUom } from './QuantityWithUom'

type InventoryBadgeType = 'success' | 'warning' | 'danger'

// The tone system (libs/webui/util/src/Status/StatusColor.scss) already carries a contrast-checked fill/text
// pair for each of these semantics in both themes — Active for success, InProgress for warning, Failure for
// danger — so this maps to it instead of the raw Bootstrap `bg-{type}-subtle` + hardcoded `text-black` pairing,
// which put black text on the dark theme's near-black subtle fills.
const TONE_BY_TYPE: Record<InventoryBadgeType, StatusToneEnum> = {
  success: StatusToneEnum.Active,
  warning: StatusToneEnum.InProgress,
  danger: StatusToneEnum.Failure,
}

type InventoryBadgeProps = {
  type: InventoryBadgeType
  title: string
  icon: string
  quantity: number
  symbol?: string
}

export const QuantityCountBadge = ({ type, title, icon, quantity, symbol }: InventoryBadgeProps) => (
  <StatusColorBadge
    className="py-1 px-2 border"
    pill
    color={{ tone: TONE_BY_TYPE[type] }}
    status={
      <TooltipPopOver tooltipBody={title}>
        <div className="h6 m-0">
          <Icon name={icon} className={'px-1'} />
          {symbol ? <QuantityWithUom quantity={quantity} symbol={symbol} /> : quantity}
        </div>
      </TooltipPopOver>
    }
  />
)

export type InventoryBadgesProps = {
  quantity: number
  inventoryCount: number
  symbol?: string
}

export const QuantityCountBadges = ({ quantity, inventoryCount, symbol }: InventoryBadgesProps) => {
  const qty = quantity ?? 0
  const invCount = inventoryCount ?? 0

  return (
    <HorizontalStack gap={1}>
      <QuantityCountBadge
        type="success"
        title={`Total items added to inventory: ${invCount}`}
        icon="boxes-packing"
        quantity={invCount}
        symbol={symbol}
      />
      <ShowIf condition={invCount < qty}>
        <QuantityCountBadge
          type="warning"
          title={`Remaining items to be added to inventory: ${qty - invCount}`}
          icon="dolly"
          quantity={qty - invCount}
          symbol={symbol}
        />
      </ShowIf>
      <ShowIf condition={invCount > qty}>
        <QuantityCountBadge
          type="danger"
          title={`Additional items that was added to inventory: ${invCount - qty}`}
          icon="dolly"
          quantity={invCount - qty}
          symbol={symbol}
        />
      </ShowIf>
    </HorizontalStack>
  )
}
